/*
  Professor Pure: keep the chat button clear of the sticky add-to-cart bar.

  While a sticky add-to-cart bar (or the theme's mobile sticky bar) is visible
  and horizontally overlaps the chat button stack (launcher + "Professor Pure"
  name card + offer preview bubble), this sets --pp-lift on <html> to the
  number of px needed to sit GAP px above the bar. professor-pure.css turns
  that into a CSS `translate`, so the button slides up and slides back down
  (to exactly where it was) when the bar hides.

  Detection is reused from assets/pss-social-proof.js (same obstacle
  selectors and visibility test). The sticky ATC bar is tracked by watching
  its visible class (sticky-atc-bar--show, toggled by the theme's own
  IntersectionObserver) through a MutationObserver, and its final resting
  position is read from its computed `bottom` + height, so the lift does not
  lag behind the bar's slide-in animation. Scroll / resize / ResizeObserver
  are fallbacks. The launcher's own resting bottom comes from its computed
  style, which already includes env(safe-area-inset-bottom) on iOS.
*/
(function () {
  'use strict';
  if (window.__ppStickyLift) return;
  window.__ppStickyLift = true;

  var GAP = 12; // px of clear space between the bar and the chat button
  var BAR_SEL = '.sticky-atc-bar, sticky-atc-bar, sticky-atc-bar-minimalist';
  var BAR_SHOW_CLASS = 'sticky-atc-bar--show';
  var OTHER_SEL = 'mobile-sticky-bar, .mobile-sticky-bar';
  var STACK_SEL = '.pp-launcher, .pp-name-card, .pp-offer-teaser';
  var root = document.documentElement;
  var current = -1;
  var queued = false;
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var mo = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;
  var ro = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

  function isShown(n) {
    var s = getComputedStyle(n);
    if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0) return false;
    var r = n.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function watch(n) {
    if (!watched || watched.has(n)) return;
    watched.add(n);
    if (mo) mo.observe(n, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    if (ro) ro.observe(n);
    n.addEventListener('transitionend', schedule);
  }

  /* Horizontal extent of the chat stack (launcher + label + bubble) at rest. */
  function stackSpan() {
    var left = Infinity, right = -Infinity;
    document.querySelectorAll(STACK_SEL).forEach(function (n) {
      var r = n.getBoundingClientRect();
      if (!r.width) return;
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    });
    return left < right ? { left: left, right: right } : null;
  }

  /* Height (px from the viewport bottom) the chat stack must clear. */
  function obstacleHeight(span) {
    var vh = window.innerHeight;
    var need = 0;
    document.querySelectorAll(BAR_SEL).forEach(function (n) {
      if (n.matches(BAR_SEL) && n.parentElement && n.parentElement.closest(BAR_SEL)) return; // nested match
      watch(n);
      var shows = n.classList.contains(BAR_SHOW_CLASS) || (n.closest('.' + BAR_SHOW_CLASS) !== null);
      if (!shows) return;
      var s = getComputedStyle(n);
      if (s.display === 'none' || s.position !== 'fixed') return;
      var r = n.getBoundingClientRect();
      if (!r.width || r.right <= span.left || r.left >= span.right) return;
      // Resting position (ignores the slide-in transform): bottom offset + height.
      var h = (parseFloat(s.bottom) || 0) + n.offsetHeight;
      need = Math.max(need, h);
    });
    document.querySelectorAll(OTHER_SEL).forEach(function (n) {
      watch(n);
      if (!isShown(n)) return;
      var r = n.getBoundingClientRect();
      if (r.top >= vh || r.right <= span.left || r.left >= span.right) return;
      need = Math.max(need, vh - r.top);
    });
    return need;
  }

  function launcherRestBottom() {
    var l = document.querySelector('.pp-launcher');
    if (!l) return null;
    var b = parseFloat(getComputedStyle(l).bottom);
    return isNaN(b) ? null : b;
  }

  var ppRootWatched = null;
  function watchRoot() {
    var pp = document.getElementById('pp-root');
    if (!mo || !pp || pp === ppRootWatched) return;
    ppRootWatched = pp;
    new MutationObserver(schedule).observe(pp, { childList: true });
  }

  function update() {
    queued = false;
    watchRoot();
    var base = launcherRestBottom();
    var span = stackSpan();
    var lift = 0;
    if (base !== null && span) {
      var need = obstacleHeight(span);
      if (need > 0) lift = Math.max(0, Math.ceil(need + GAP - base));
    }
    if (lift !== current) {
      current = lift;
      root.style.setProperty('--pp-lift', lift + 'px');
      root.classList.toggle('pp-lifted', lift > 0);
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  function start() {
    // Re-evaluate when the chat widget adds/removes its launcher or bubble,
    // or when sections are re-rendered.
    if (mo) {
      new MutationObserver(schedule).observe(document.body, { childList: true, attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('load', schedule);
    document.addEventListener('shopify:section:load', schedule);
    schedule();
    // The chat widget builds its launcher after this script may run.
    setTimeout(schedule, 500);
    setTimeout(schedule, 2000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
